/**
 * The customer's outstanding change requests (prototype `vRevision`'s table).
 *
 * Each row's action depends on what was asked: a quantity change, a removal, or an added item
 * (once a supplier is chosen) QUEUE into the caller's batch rather than hitting the server —
 * `apply_revisions` bumps the revision on every call, and resolving requests one row at a time
 * must not do that once per row. A reprice is the one exception: it has no `apply_revisions` op
 * at all and goes straight through the line's own PATCH, so it alone still shows a real busy
 * spinner and applies immediately.
 *
 * A queued row shows that state and an Undo, exactly like the embedded quotation's own rows
 * (`QuoteRow.tsx`) — both draw on the same batch, so either surface can discard what the other
 * queued.
 *
 * Applied rows stay visible, struck through and dimmed, so the round reads as a record of
 * what was asked and what was done.
 */

import { useState } from 'react'
import { Badge } from '../../components/Badge'
import { Button, Card } from '../../components/ui'
import { REVISION_LABELS } from '../../lib/revisions'
import type { Item, RevisionRequest } from '../../lib/types'

/**
 * A reprice needs a NEW rate — applying the price already on the line would close the
 * customer's "can you do better?" having changed nothing. Re-source opens the comparison;
 * the input is for when the purchaser already knows the number they can do.
 */
function RepriceAction({
  request,
  busy,
  onApply,
  onResource,
}: {
  request: RevisionRequest
  busy: boolean
  onApply: (request: RevisionRequest, newRate?: number) => void
  onResource: (request: RevisionRequest) => void
}) {
  const [rate, setRate] = useState('')
  const parsed = Number(rate)
  const valid = rate.trim() !== '' && Number.isFinite(parsed) && parsed > 0

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <Button size="sm" onClick={() => onResource(request)}>
        Re-source
      </Button>
      <input
        type="number"
        step="0.01"
        value={rate}
        placeholder="new rate"
        aria-label={`New rate for ${request.item_code}`}
        style={{ width: 90 }}
        onChange={(e) => setRate(e.target.value)}
      />
      <Button size="sm" variant="primary" disabled={busy || !valid} onClick={() => onApply(request, parsed)}>
        {busy ? 'Applying…' : 'Apply'}
      </Button>
    </div>
  )
}

export function RevisionRequestsTable({
  requests,
  itemsByCode,
  currentQtyOf,
  busyRequestId,
  pending,
  onApply,
  onDiscard,
  onChooseSupplier,
  onResource,
}: {
  requests: RevisionRequest[]
  itemsByCode: Map<string, Item>
  /** The quantity currently on the quotation for a request's item, when it has a line. */
  currentQtyOf: (request: RevisionRequest) => number | null
  /** Only `reprice` still uses this — everything else queues instead of going in flight. */
  busyRequestId: string | null
  /** The caller's batch, keyed by item_code — same one the embedded quotation's rows read. */
  pending: Map<string, { op: 'set_qty' | 'remove' | 'add'; qty: number }>
  onApply: (request: RevisionRequest, newRate?: number) => void
  /** Un-queues a request's edit — a no-op if it was never queued (nothing to discard). */
  onDiscard: (request: RevisionRequest) => void
  onChooseSupplier: (request: RevisionRequest) => void
  onResource: (request: RevisionRequest) => void
}) {
  return (
    <Card padded={false}>
      <table>
        <thead>
          <tr>
            <th>Request</th>
            <th>Item</th>
            <th>Detail</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {requests.length === 0 ? (
            <tr>
              {/*
                Says "none recorded", NOT "the customer hasn't asked for any" — which is what this
                used to claim and can no longer be inferred. Nothing in the app writes a revision
                request any more: logging one was a form standing between the purchaser and the
                edit, when the quotation below is directly editable. `get_revisions` therefore
                returns `[]` unless the BACKEND populates the log from a customer's mail.

                So an empty table means "nothing was recorded here", which is true, rather than a
                claim about what the customer did — a claim this screen has no evidence for.
              */}
              <td colSpan={4} style={{ color: 'var(--muted)', textAlign: 'center', padding: 22 }}>
                No revision requests recorded. Edit the quotation below directly — quantities, prices, and
                added or removed lines all apply as one revision.
              </td>
            </tr>
          ) : null}

          {requests.map((request) => {
            // An unrecognised type must degrade to a plain row, not throw and blank the page.
            const label = REVISION_LABELS[request.type] ?? { title: request.type, tone: 'muted' as const }
            const done = request.status === 'done'
            const busy = busyRequestId === request.id
            const item = request.item_code ? itemsByCode.get(request.item_code) : undefined
            // Queued: this request's item already has a pending edit in the caller's batch —
            // resolved, but not yet sent. `reprice` never lands here (it is never queued).
            const queued = request.item_code ? pending.get(request.item_code) : undefined

            return (
              <tr key={request.id} style={done ? { opacity: 0.55 } : undefined}>
                <td>
                  <Badge tone={label.tone}>{label.title}</Badge>
                </td>

                <td style={{ fontWeight: 600, textDecoration: done ? 'line-through' : undefined }}>
                  {item?.name ?? request.item_code ?? '—'}
                  {request.qty !== null ? (
                    <span style={{ color: 'var(--muted)', fontWeight: 500 }}>
                      {/* The transition, not just the target — "300 → 500" is what the
                          purchaser needs to judge the change (prototype line 382). */}
                      {' · '}
                      {currentQtyOf(request) !== null ? `${currentQtyOf(request)} → ` : ''}
                      {request.qty}
                    </span>
                  ) : null}
                </td>

                <td style={{ color: 'var(--muted)' }}>{request.detail}</td>

                <td>
                  {done ? (
                    <Badge tone="ok">✓ applied</Badge>
                  ) : request.type === 'reprice' ? (
                    <RepriceAction request={request} busy={busy} onApply={onApply} onResource={onResource} />
                  ) : queued ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <Badge tone="warn">Queued</Badge>
                      <Button size="sm" onClick={() => onDiscard(request)}>
                        Undo
                      </Button>
                    </div>
                  ) : request.type === 'add' ? (
                    <Button size="sm" variant="primary" onClick={() => onChooseSupplier(request)}>
                      Choose supplier
                    </Button>
                  ) : (
                    <Button size="sm" variant="primary" onClick={() => onApply(request)}>
                      Queue
                    </Button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </Card>
  )
}
